import type { LanguageCodeEnum, OrdersQuery } from '@/graphql/saleor/api';
import { fetchStorefrontBffWithAuth } from '@/lib/auth';
import { fetchOrderData } from '@/lib/order';
import { paths } from '@/lib/paths';

export const checkIsOrderPaidOrHasOfflinePayment = async (
  id: string,
  languageCode: LanguageCodeEnum,
) => {
  const res = await fetchOrderData(id, languageCode);
  const order = res.data.order;

  if (!order) return false;

  const paidAmount =
    (order.totalAuthorized?.amount || 0) + (order.totalCharged?.amount || 0);

  return paidAmount > 0 || order.isOfflinePayment?.toLowerCase() === 'true';
};

export const getUserCheckout = async (channel: string) => {
  try {
    const result = await fetchStorefrontBffWithAuth(
      paths.api.checkoutUser.asPath({ query: { channel } }),
    );

    if (!result.ok) {
      return null;
    }

    const data = await result.json();
    return data?.checkout;
  } catch (error) {
    console.error('Error fetching user checkout:', error);
    return null;
  }
};

export const getCheckoutOrderId = async (
  checkoutId: string,
  channel: string,
) => {
  try {
    const result = await fetchStorefrontBffWithAuth(
      paths.api.order.getByCheckout.asPath({
        query: { checkoutId, channel },
      }),
    );

    if (!result.ok) {
      return null;
    }

    const { data } = await result.json();
    return data?.orders?.edges?.[0]?.node.id;
  } catch (error) {
    console.error('Error fetching user checkout:', error);
    return null;
  }
};

export const fetchUserCheckoutContext = async (channel: string) => {
  const userCheckout = await getUserCheckout(channel);
  const checkoutOrders = userCheckout?.id
    ? await getCheckoutOrders(userCheckout.id, channel)
    : undefined;

  return { checkoutOrders, userCheckout };
};

export const getCheckoutOrders = async (
  checkoutId: string,
  channel: string,
) => {
  try {
    const result = await fetchStorefrontBffWithAuth(
      paths.api.order.getByCheckout.asPath({
        query: { checkoutId, channel },
      }),
    );

    if (!result.ok) {
      return null;
    }

    const { data } = await result.json();
    return data?.orders?.edges as NonNullable<OrdersQuery['orders']>['edges'];
  } catch (error) {
    console.error('Error fetching user checkout:', error);
    return null;
  }
};
